//------------------------------------------------------------------------------
// Note on fieldDef.dataTypes: Data types are defined in the reports collection
//  fields array.  Data types include standard string, number and boolean types 
//  as well as custom grade, date and list types.  The custom types allow us to
//  handle sorts, filters and renders slightly differently for these types.
//------------------------------------------------------------------------------

import { Form, Button, Col, Input, message, Popconfirm, Row, Select, Tooltip, TreeSelect, Typography } from "antd";
import { Link } from 'react-router-dom';
import dayjs from 'dayjs'
import DOMPurify from 'dompurify';

//--------
//Normalize report table data
//--------
export const normalizeReportTableData = async (reportTableData, reportDefRef) => {
  if (!reportTableData || reportTableData?.length === 0) {return []}

  const fieldDefs = reportDefRef?.selectedFields

  const updatedReportTableData = reportTableData.map((row) => {
    const newRow = {...row}
    fieldDefs.map((fieldDef) => {
      //Handle date dataTypes
      switch (String(fieldDef.dataType).toLowerCase()) {
        case 'string':
        case 'grade':
          newRow[fieldDef.fieldName] = row[fieldDef.fieldName] ? String(row[fieldDef.fieldName]) : null;
          break
        case 'date':
          newRow[fieldDef.fieldName] = row[fieldDef.fieldName] ? dayjs((row[fieldDef.fieldName])) : null;
          break
        case 'number':
          newRow[fieldDef.fieldName] = !Number.isNaN(row[fieldDef.fieldName]) ? Number(row[fieldDef.fieldName]) : 0;
          break
        case 'boolean':
          newRow[fieldDef.fieldName] = row[fieldDef.fieldName] === true ? true : false;
          break
        case 'list':
          newRow[fieldDef.fieldName] = row[fieldDef.fieldName]
          break
        case 'html':
          newRow[fieldDef.fieldName] = row[fieldDef.fieldName]
          break
        default:
          newRow[fieldDef.fieldName] = row[fieldDef.fieldName] ? String(row[fieldDef.fieldName]) : null;
          break
      }
    })
    return newRow
    }
  )
  return updatedReportTableData
}

export const normalizeReportTableDataForExport = async (reportTableData, reportDefRef) => {
  if (!reportTableData || reportTableData?.length === 0) {return []}

  const fieldDefs = reportDefRef?.selectedFields

  const updatedReportTableData = reportTableData.map((row) => {
    const newRow = {...row}
    fieldDefs.map((fieldDef) => {
      //Handle date dataTypes
      switch (String(fieldDef.dataType).toLowerCase()) {
        case 'string':
        case 'grade':
          newRow[fieldDef.fieldName] = row[fieldDef.fieldName] ? String(row[fieldDef.fieldName]) : '';
          break
        case 'date':
          newRow[fieldDef.fieldName] = row[fieldDef.fieldName] ?  dayjs(String(row[fieldDef.fieldName])).format('MM/DD/YYYY') : '';
          break
        case 'number':
          newRow[fieldDef.fieldName] = !Number.isNaN(row[fieldDef.fieldName]) ? Number(row[fieldDef.fieldName]).toLocaleString() : 0;
          break
        case 'boolean':
          newRow[fieldDef.fieldName] = row[fieldDef.fieldName] === true ? 'Yes' : 'No';
          break
        case 'list':
          newRow[fieldDef.fieldName] = Array.isArray(newRow[fieldDef.fieldName]) ? newRow[fieldDef.fieldName].join(' <|> ') : ''
          break
        case 'html':
          newRow[fieldDef.fieldName] = row[fieldDef.fieldName] ? String(row[fieldDef.fieldName].replace(/<[^>]*>/g, '')) : ''
          break
        default:
          newRow[fieldDef.fieldName] = row[fieldDef.fieldName] ? String(row[fieldDef.fieldName]) : '';
          break
      }
    })
    return newRow
    }
  )
  return updatedReportTableData
}

//--------
// Equate null, undefined, empty for comparisons
//--------
export const isEffectivelyEmpty = (value) => {
  return (
    value === null ||
    value === undefined ||
    (typeof value === 'string' && value.trim() === '') ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0)
  )
}

export const normalizedValue = (value) => {
  if (value === null) {return ''}
  if (value === undefined) {return ''}
  if (typeof value === 'string' && value.trim() === '') {return ''}
  if (Array.isArray(value) && value.length === 0) {return []}
  if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) {return {}}
  return value
}

export const isReportDefChanged = (original, updated) => {
  if (!original || !updated) {return false}

  //reportTitle
  const normalizedOriginalReportTitle = normalizedValue(original.reportTitle)
  const normalizedUpdatedReportTitle = normalizedValue(updated.reportTitle)
  if (normalizedOriginalReportTitle !== normalizedUpdatedReportTitle) {return true}

  //reportNotes
  const normalizedOriginalReportNotes = normalizedValue(original.reportNotes)
  const normalizedUpdatedReportNotes = normalizedValue(updated.reportNotes)
  if (normalizedOriginalReportNotes !== normalizedUpdatedReportNotes) {return true}
// console.log('after title/notes')
  //selectedFields
  const originalSelectedFields = Array.isArray(original?.selectedFields) ? original.selectedFields.map(field => field.fieldName) : []
  const updatedSelectedFields = Array.isArray(updated?.selectedFields) ? updated.selectedFields.map(field => field.fieldName) : []
  if (originalSelectedFields.length !== updatedSelectedFields.length) {return true}

  const selectedFieldsMatches = originalSelectedFields.map((field, index) => field === updatedSelectedFields[index])
  if (!selectedFieldsMatches.every(Boolean)) {return true}
  const updatedFieldsMatches = updatedSelectedFields.map((field, index) => field === originalSelectedFields[index])
  if (!updatedFieldsMatches.every(Boolean)) {return true}

  // console.log('after selecgtedFields')

  //reportParameters.  Order doesn't matter.
  //const knownParms = ['selectedSchoolIds', 'selectedDistrictIds'];
  let originalSelectedIds = []
  let updatedSelectedIds = []
  if (original?.reportParameters) {
    const originalReportParametersKeys = Object.keys(original.reportParameters)
    if (originalReportParametersKeys.length > 0) {
      originalSelectedIds = original.reportParameters[originalReportParametersKeys[0]]
    }
    // originalSelectedIds = knownParms.map((key) => original.reportParameters[key]).find((key) => Array.isArray(key))
  }
  if (updated?.reportParameters) {
    const updatedReportParametersKeys = Object.keys(updated.reportParameters)
    if (updatedReportParametersKeys.length > 0) {
      updatedSelectedIds = updated.reportParameters[updatedReportParametersKeys[0]]
    }
    //   updatedSelectedIds = knownParms.map((key) => updated.reportParameters[key]).find((key) => Array.isArray(key))
  }
  if (originalSelectedIds.length !== updatedSelectedIds.length) {return true}

  const originalSelectedIdsSorted = [...originalSelectedIds].sort();
  const updatedSelectedIdsSorted = [...updatedSelectedIds].sort();
  const selectedIdsChanged = !originalSelectedIdsSorted.every((value, index) => value === updatedSelectedIdsSorted[index]);
  if (selectedIdsChanged) {return true}
  // console.log('after parms',originalSelectedIdsSorted,updatedSelectedIdsSorted,selectedIdsChanged,)

  //Page layout.  Assume object is well structured.
  const originalPageLayout = original?.pageLayout ? original.pageLayout : []
  const updatedPageLayout = updated?.pageLayout ? updated.pageLayout : []
  const pageLayoutKeys = Object.keys(originalPageLayout);

  let pageLayoutChanged = false
  for (const key of pageLayoutKeys) {
    const originalValue = originalPageLayout[key] ?? ''
    const updatedValue = updatedPageLayout[key] ?? ''
    if (originalValue !== updatedValue) {
      pageLayoutChanged = true
      break
    }
  }
  if (pageLayoutChanged) {return true}
  // console.log('after pagelayout',originalPageLayout,updatedPageLayout,pageLayoutChanged)

  //Distribution
  const originalDistribution = original?.distribution ? original.distribution : []
  const updatedDistribution = updated?.distribution ? updated.distribution : []

  const normalizedOriginalEmailFrom = normalizedValue(originalDistribution.emailFrom)
  const normalizedUpdatedEmailFrom = normalizedValue(updatedDistribution.emailFrom)
  if (normalizedOriginalEmailFrom !== normalizedUpdatedEmailFrom) {return true} //From email changed

  const normalizedOriginalEmailSubject = normalizedValue(originalDistribution.emailSubject)
  const normalizedUpdatedEmailSubject = normalizedValue(updatedDistribution.emailSubject)
  if (normalizedOriginalEmailSubject !== normalizedUpdatedEmailSubject) {return true} //Email subject changed

  const normalizedOriginalEmailText = normalizedValue(originalDistribution.emailText)
  const normalizedUpdatedEmailText = normalizedValue(updatedDistribution.emailText)
  if (normalizedOriginalEmailText !== normalizedUpdatedEmailText) {return true} //Email text changed
  
  const originalEmailRecipients = originalDistribution?.emailRecipients ? originalDistribution.emailRecipients : []
  const updatedEmailRecipients = updatedDistribution?.emailRecipients ? updatedDistribution.emailRecipients : []
  if (originalEmailRecipients.length !== updatedEmailRecipients.length) {return true} //Different number of email recipients

  const updatedEmailSet = new Set(updatedEmailRecipients.map(email => String(email).trim().toLowerCase()));
  const emailMatchCount =  originalEmailRecipients.filter(email => updatedEmailSet.has(String(email).trim().toLowerCase())).length;
  if (!(originalEmailRecipients.length === updatedEmailRecipients.length  && updatedEmailRecipients.length === emailMatchCount)) {return true} //All counts should match if attachments are the same

  const originalEmailAttachments = originalDistribution?.emailAttachments ? originalDistribution.emailAttachments : []
  const updatedEmailAttachments = updatedDistribution?.emailAttachments ? updatedDistribution.emailAttachments : []
  if (originalEmailAttachments.length !== updatedEmailAttachments.length) {return true} //Different number of email attachements

  const updatedAttachmentsSet = new Set(updatedEmailAttachments.map(type => String(type).trim().toLowerCase()));
  const attachmentsMatchCount =  originalEmailAttachments.filter(type => updatedAttachmentsSet.has(String(type).trim().toLowerCase())).length;
  if (!(originalEmailAttachments.length === updatedEmailAttachments.length  && updatedEmailAttachments.length === attachmentsMatchCount)) {return true} //All counts should match if attachments are the same
  // console.log('after distribution')

  //Sorter
  const originalSorter = original?.reportTableSorter ? original.reportTableSorter : null
  const updatedSorter = updated?.reportTableSorter ? updated.reportTableSorter : null

  if((originalSorter?.field && !updatedSorter?.field) || (!originalSorter?.field && updatedSorter?.field)) {return true}
  if((originalSorter?.field && updatedSorter?.field) && originalSorter?.field !== updatedSorter?.field) {return true}
  if((originalSorter?.order && !updatedSorter?.order) || (!originalSorter?.order && updatedSorter?.order)) {return true}
  if((originalSorter?.order && updatedSorter?.order) && originalSorter?.order !== updatedSorter?.order) {return true}
  // console.log('sorttests',originalSorter === updatedSorter,originalSorter===null,updatedSorter===null)
  if(originalSorter !== updatedSorter) {return true}
  // console.log('after sorter',originalSorter,updatedSorter,originalSorter === updatedSorter)

  //Filters
  const originalFilters = original?.reportTableFilters ? original.reportTableFilters : []
  const updatedFilters = updated?.reportTableFilters ? updated.reportTableFilters : []
  
  const originalFilterKeys = Object.keys(originalFilters);
  const updatedFilterKeys = Object.keys(updatedFilters);
  if (originalFilterKeys.length !== updatedFilterKeys.length) return true;

  const filtersChanged = originalFilterKeys.some((key) => {
    const val1 = originalFilters[key] ?? [];
    const val2 = updatedFilters[key] ?? [];

    if (!Array.isArray(val1) || !Array.isArray(val2)) return true;
    if (val1.length !== val2.length) return true;

    const sorted1 = [...val1].sort();
    const sorted2 = [...val2].sort();
    return sorted1.some((v, i) => v !== sorted2[i]);
  });
  return filtersChanged
  // console.log('after dilrwea')

  //Will need report table columns/selected fields check when/if "Report Column Detail" is implemented.
  //e.g. loop through columns/selected fields and find any changed header captions, alignment, sorts, filters, etc.

  return false
}



function shouldSkipKey(key) {
  return ['__typename', '_id', 'createdAt', 'updatedAt'].includes(key);
}

// function arraysEqualIgnoreOrder(a, b) {
//   if (a.length !== b.length) return false;
//   const visited = new Array(b.length).fill(false);

//   return a.every(itemA =>
//     b.some((itemB, i) => !visited[i] && areObjectsDifferent(itemA, itemB) && (visited[i] = true))
//   );
// }

export const areObjectsDifferent = (a, b) => {
  if (isEffectivelyEmpty(a) && isEffectivelyEmpty(b)) return true;
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (a === null || b === null) return false;
  if (typeof a !== 'object') return a === b;

  // Arrays (order matters)
  if (Array.isArray(a)) {
    if (!Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    return a.every((item, i) => areObjectsDifferent(item, b[i]));
  }

  // Objects
  const keysA = Object.keys(a).filter(k => !shouldSkipKey(k));
  const keysB = Object.keys(b).filter(k => !shouldSkipKey(k));

  if (keysA.length !== keysB.length) return false;

  return keysA.every(key => areObjectsDifferent(a[key], b[key]));
}


//--------
// Find differences between objects
// Used for determining whether a user has made changes to data
//--------
export const findObjectDifferences = (original, updated, path = []) => {
  const changes = [];
  const cleanedOriginal = removeTypenameRecursively(original)
  const cleanedUpdated = removeTypenameRecursively(updated)

  // Handle primitives and null
  if (typeof cleanedOriginal !== 'object' || cleanedOriginal === null || typeof cleanedUpdated !== 'object' || cleanedUpdated === null) {
    if (cleanedOriginal !== cleanedUpdated) {
      changes.push({ path: path.join('.'), from: cleanedOriginal, to: cleanedUpdated });
    }
    return changes;
  }

  // Handle arrays
  if (Array.isArray(cleanedOriginal) || Array.isArray(cleanedUpdated)) {
    // console.log(cleanedOriginal,cleanedUpdated)
    if (JSON.stringify(cleanedOriginal) != JSON.stringify(cleanedUpdated)) {
      changes.push({ path: path.join('.'), from: cleanedOriginal, to: cleanedUpdated });
    }
    return changes;
  }

  // Recurse into objects
  const allKeys = new Set([...Object.keys(cleanedOriginal), ...Object.keys(cleanedUpdated)]);
  for (const key of allKeys) {
    const oVal = cleanedOriginal[key];
    const uVal = cleanedUpdated[key];
    changes.push(...findObjectDifferences(oVal, uVal, [...path, key]));
  }

  return changes;
}

//--------
//Combines null, undefined and an empty string into a single "__EMPTY__" value
//Used with column filter values to combine multiple "blank" values in the list into a single "{Empty) entry"
//--------
export const normalizeFilterValue = (value) => {
  return value === null || value === undefined || value === "" ? "__EMPTY__" : value;
};

//--------
//Extracts the unique values using the passed fieldName and passed newReportData and returns them
//as an antd column filter object
//--------
export const getReportTableColumnFilterValues = (fieldName, newReportData, reportDefRef) => {
  if (!newReportData || newReportData?.length === 0) {
    return []
    //newReportData = []
    // newReportData = reportTableExtraData
    return
  }

  //Get field def
  const selectedFields = reportDefRef?.current?.selectedFields
  const fieldDef = selectedFields.find((field) => field.fieldName === fieldName)
  if (!fieldDef) {return []}

  //Handle data types
  let uniqueValues=[]
  let antdFilters = []
  switch  (fieldDef.dataType.toLowerCase()) {
    case 'date':
      const formattedDateValues = newReportData.map((row) => {
        if (dayjs.isDayjs(row[fieldDef?.fieldName])) {
          return row[fieldDef?.fieldName].format('YYYY-MM-DD')
        } else {
          return null
        }
      })
      uniqueValues = [...new Set(formattedDateValues.map((date) => normalizeFilterValue(date)))];
      antdFilters = uniqueValues.map((value) => ({
        text: dayjs(value).format('MM/DD/YYYY'),
        value: value,
      }))
      break
    case 'string':
    case 'number':
    case 'grade':
      uniqueValues = [...new Set(newReportData.map((row) => normalizeFilterValue(row[fieldName])))];
      antdFilters = uniqueValues.map((value) => ({
        text: value === "__EMPTY__" ? "(Empty)" : String(value),
        value: value,       
      }));
      break
    case 'boolean':
      uniqueValues = [...new Set(newReportData.map((row) => normalizeFilterValue(row[fieldName])))];
      antdFilters = uniqueValues.map((value) => ({
        text: value === "__EMPTY__" ? "(Empty)" : value === true ? 'Yes' : 'No',
        value: value,       
      }));
      break
    case 'list':
      const flatValues = Array.from(new Set(newReportData.flatMap(row => row[fieldName])))
      uniqueValues = [...new Set(flatValues.map((value) => normalizeFilterValue(value)))];
      antdFilters = uniqueValues.map((value) => ({
        text: value === "__EMPTY__" ? "(Empty)" : String(value),
        value: value,       
      }));
      // console.log('getFilterValkues',fieldName,flatValues,uniqueValues)
      break
    case 'html':
      uniqueValues = [...new Set(newReportData.map((row) => normalizeFilterValue(String(row[fieldName]).replace(/<[^>]*>/g, ''))))];
      antdFilters = uniqueValues.map((value) => ({
        text: value === "__EMPTY__" ? "(Empty)" : String(value.replace(/<[^>]*>/g, '')),
        value: value,       
      }));
      break
    default:
      break
  }

  //Sort by value
  const testType = fieldName==='studentGrade' ? 'grade' : fieldDef.dataType.toLowerCase()
  const sortedFilters = sortObjectArray(antdFilters,'value',testType,'asc')

  return sortedFilters
}

//--------
//Get a report table column's render value
//--------
export const getReportTableColumnRender = (fieldValue, record, baseFieldDef) => {
  if (!baseFieldDef) {return null}

  //Handle a linked field
  let value = fieldValue
  let valueType = typeof value
  if (baseFieldDef.linkParms) {
    const pathIdValue = record[baseFieldDef.linkParms.pathIdField]
    const linkPath = baseFieldDef.linkParms.basePath + pathIdValue
    const linkText = record[baseFieldDef.linkParms.textField]
    return <Tooltip title={baseFieldDef.linkParms.tooltipText}>
        <Link to={linkPath}>
          {linkText}
        </Link>
      </Tooltip>
  }

  //Handle data types
  let renderValue =''
  switch  (baseFieldDef.dataType.toLowerCase()) {
    case 'string':
    case 'grade':
      renderValue = value ? String(value) : ''
      break
    case 'number':
      renderValue = value ? value.toLocaleString() : 0
      // renderValue = value ? String(value) : 0
      break
    case 'date':
      renderValue = dayjs(value).format('MM/DD/YYYY')
      break
    case 'boolean':
      renderValue = value === true ? 'Yes' : 'No'
      break
    case 'list':
      renderValue = Array.isArray(value) ? value.join(' <|> ') : ''
      break
    case 'html':
      // renderValue = value ? value.replace(/<[^>]*>/g, '') : ''  //To remove markup
      // const cleanHtml = DOMPurify.sanitize(value, {ALLOWED_TAGS: ['b', 'q'], ALLOWED_ATTR: ['style']});

      const cleanHtml =  DOMPurify.sanitize(value);
      renderValue =  <div dangerouslySetInnerHTML={{ __html: cleanHtml }} />
      break
    default:
      renderValue = String(value)
      break
  }

  return renderValue
}


//--------
//Perform report table filter on a column
//--------
export const handleReportTableFilter = (value, record, fieldName, reportDefRef) => {
  const selectedFields = reportDefRef?.current?.selectedFields
  const fieldDef = selectedFields.find((field) => field.fieldName === fieldName)
  if (!fieldDef) {return 0}

  // console.log('in handleReportTableFilter',value,record,fieldName,reportDefRef)
  let recordValue, normalizedValue = ''
  let filterResult = true
  switch  (fieldDef.dataType.toLowerCase()) {
    case 'date':
      recordValue = normalizeFilterValue(dayjs(record[fieldName]).format('YYYY/MM/DD'))
      normalizedValue = normalizeFilterValue(dayjs(value).format('YYYY/MM/DD'));
      filterResult = (normalizedValue === recordValue)
      break
    case 'string':
    case 'number':
    case 'grade':
      recordValue = normalizeFilterValue(record[fieldName])
      normalizedValue = normalizeFilterValue(value)
      filterResult = (normalizedValue === recordValue)
      break
    case 'boolean':
      recordValue = record[fieldName] ? normalizeFilterValue(record[fieldName]) : false
      normalizedValue = value ? normalizeFilterValue(value) : false
      filterResult = (normalizedValue === recordValue)
      break
    case 'list':
      recordValue = normalizeFilterValue(record[fieldName])
      normalizedValue = normalizeFilterValue(value)
      // console.log(value,normalizedValue,record[fieldName],recordValue)
      if (normalizedValue === '__EMPTY__' && recordValue?.length === 0) { //Is selected filter "empty" and list array is empty
        filterResult = true
      } else {
        filterResult = Array.isArray(recordValue) ? recordValue.includes(normalizedValue) : false
      }
      break
    case 'html':
      recordValue = normalizeFilterValue(record[fieldName].replace(/<[^>]*>/g, ''))
      normalizedValue = normalizeFilterValue(value.replace(/<[^>]*>/g, '') )
      filterResult = (normalizedValue === recordValue)
          // renderValue = value ? value.replace(/<[^>]*>/g, '') : ''  //To remove markup

      break
    default:
      recordValue = String(record[fieldName])
      normalizedValue = String(record[fieldName])
      filterResult = (normalizedValue === recordValue)
  }

  return filterResult;
}

//--------
//Perform a report table column sort
//--------
export const handleReportTableSort = (a, b, fieldName, reportDefRef) => {
  const selectedFields = reportDefRef?.current?.selectedFields
  const fieldDef = selectedFields.find((field) => field.fieldName === fieldName)
  if (!fieldDef) {return 0}
  //console.log('sort',fieldDef.dataType)

  const valA = a[fieldName];
  const valB = b[fieldName];

  // Handle undefined/null first
  if (valA == null && valB == null) return 0;
  if (valA == null) return -1;
  if (valB == null) return 1;

  // Date type
  if (fieldDef.dataType.toLowerCase() === 'date') {
    return dayjs(valA).valueOf() - dayjs(valB).valueOf(); // or valA.diff(valB)
  }

  if (fieldDef.dataType.toLowerCase() === 'grade') {
    // console.log('sort grade',valA,getNumericGrade(valA))
    return getNumericGrade(valA) - getNumericGrade(valB)
  }

  if (fieldDef.dataType.toLowerCase() === 'list') {
    // console.log('sort list',valA,valB)
    if (Array.isArray(valA) && Array.isArray(valB)) {
      return valA.join(', ').localeCompare(valB.join(', '))
    } else {
      return String(valA).toLowerCase().localeCompare(String(valB).toLowerCase());
    }
    // sorter: (a, b) => a.grades.join(',').localeCompare(b.grades.join(','));
  }

  // If both are booleans
  if (typeof valA === 'boolean' && typeof valB === 'boolean') {
    return Number(valA) - Number(valB);
  }

  // If both are numbers
  if (typeof valA === 'number' && typeof valB === 'number') {
    return valA - valB;
  }

  // Fallback: Compare as lowercase strings
  return String(valA).toLowerCase().localeCompare(String(valB).toLowerCase());
};

//--------
//Generic sort routine for array of objects
//--------
export const sortObjectArray = (data, fieldName, dataType = 'string', direction = 'asc') => {
  // console.log('sortObject',data,fieldName,dataType,direction)
  const sorted = [...data].sort((a, b) => {
    const valA = a[fieldName];
    const valB = b[fieldName];

    // Null/undefined handling
    if (valA == null && valB == null) return 0;
    if (valA == null) return 1;
    if (valB == null) return -1;

    // Check for Day.js dates
    const isDayjsA = dayjs.isDayjs(valA);
    const isDayjsB = dayjs.isDayjs(valB);

    if (isDayjsA && isDayjsB) {
      return valA.valueOf() - valB.valueOf(); // or valA.diff(valB)
    }

    if (dataType === 'grade') {
      return getNumericGrade(valA) - getNumericGrade(valB)
    }
    
    // Native types
    if (typeof valA === 'string' && typeof valB === 'string') {
      return valA.localeCompare(valB);
    }

    if (typeof valA === 'boolean' && typeof valB === 'boolean') {
      return valA === valB ? 0 : valA ? -1 : 1;
    }

    if (typeof valA === 'number' && typeof valB === 'number') {
      return valA - valB;
    }

    // Fallback: compare as strings
    return String(valA).localeCompare(String(valB));
  });

  return direction === 'desc' ? sorted.reverse() : sorted;
};

//--------
//Get a numeric grade given character grade
//--------
export const getNumericGrade = (grade) => {
  let gradeValue = -99
  if (grade != undefined || null) {
    if (grade.slice(0, 5) === 'Grade') {
      gradeValue = parseInt(grade.replace('Grade', '').trim()) //Grade 1-12
    } else {
      switch (grade?.toLowerCase()) {
        case 'early childhood':
          gradeValue = -3
          break
        case 'pre kindergarten':
          gradeValue = -2
          break
        case 'kindergarten':
          gradeValue = -1
          break
        default:
          gradeValue = -98
          break
      }
    }
  }
  return gradeValue
}

  //--------
  //Our apollo server is configured to automatically append __typename to query responses
  //This removes it.
  //probably should move to common functions and import
  //--------
  export const removeTypename = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map(removeTypename);
    } else if (obj !== null && typeof obj === "object") {
      return Object.fromEntries(
        Object.entries(obj)
          .filter(([key]) => key !== "__typename")
          .map(([key, value]) => [key, removeTypename(value)])
      );
    }
    return obj;
  };

  //--------
  //Recursively remove __typename
  //probably should move to common functions and import
  //--------
  export const removeTypenameRecursively = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map(removeTypenameRecursively);
    } else if (obj !== null && typeof obj === "object") {
      return Object.fromEntries(
        Object.entries(obj)
          .filter(([key]) => key !== "__typename")
          .map(([key, value]) => [key, removeTypenameRecursively(value)])
      );
    }
    return obj;
  };

  export const formatPhoneNumber = (phone) => {
    if (!phone) return "";
    const cleaned = ("" + phone).replace(/\D/g, ""); // Remove non-numeric characters
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    return match ? `(${match[1]}) ${match[2]}-${match[3]}` : phone;
  };